import { useCallback, useMemo } from "react";

import { toOptionItems } from "@shared";
import * as Yup from "yup";

import { SystemDropdownCategory, SystemDropdownParameter } from "@/api";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { AppointmentAttendanceStatus } from "@/pages/clients/client/components/tabs/notes-tab/CaseNotes.types";
import {
  CaseNoteFormValues,
  CaseNoteMaxLength,
} from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-form/CaseNoteForm.types";
import { useTranslation } from "@/translations";

export function useCaseNoteForm() {
  const { t } = useTranslation();

  const { caseNoteLocations, caseNoteResults } = useDropdownOptions();
  const { optionItems: typeOptions, booleanParameterOf } = useSystemDropdownOptions(
    SystemDropdownCategory.CaseNoteType
  );
  const locationOptions = useMemo(() => toOptionItems(caseNoteLocations), [caseNoteLocations]);
  const resultOptions = useMemo(() => toOptionItems(caseNoteResults), [caseNoteResults]);
  const appointmentStatusOptions = useMemo<IOptionItem[]>(
    () =>
      Object.values(AppointmentAttendanceStatus).map((status) => ({
        id: status,
        label: t(`clients.client.caseNotes.appointmentStatus.${status}`),
      })),
    [t]
  );

  const isConversationType = useCallback(
    (type: string | null) => booleanParameterOf(type, SystemDropdownParameter.Conversation),
    [booleanParameterOf]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape<{ [K in keyof CaseNoteFormValues]?: Yup.AnySchema }>({
        type: Yup.string()
          .required(t("common.validation.fieldRequired", { fieldName: t("clients.client.caseNotes.form.type") }))
          .max(CaseNoteMaxLength.noteType, ({ max }) => t("common.validation.maxLength", { max })),
        date: Yup.date().nullable(),
        assignedCounselorId: Yup.string().nullable(),
        content: Yup.string().nullable(),
        location: Yup.string()
          .nullable()
          .max(CaseNoteMaxLength.location, ({ max }) => t("common.validation.maxLength", { max })),
        result: Yup.string()
          .nullable()
          .max(CaseNoteMaxLength.result, ({ max }) => t("common.validation.maxLength", { max })),
        appointmentStatus: Yup.string().nullable(),
      }),
    [t]
  );

  return {
    typeOptions,
    locationOptions,
    resultOptions,
    appointmentStatusOptions,
    isConversationType,
    validationSchema,
  };
}
